/**
 * The buyer's entry point into the fabric catalogue -- a separate page, not a
 * search box inline in the form (chosen over the inline shape with Arun):
 * search here, pick one, land on New requirement pre-filled from it.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { Empty, Loading, Problem, measurement } from '../ui'

interface FabricSummary {
  id: string
  label: string
  fabricCode: string
  finishedGsm: number | null
  finishedWidth: string | null
  widthUnit: string | null
  construction: string | null
}

export function FabricSearch() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<FabricSummary[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  // Debounced: a keyword search fires a request per pause in typing, not per
  // keystroke against an 8,000-row table.
  useEffect(() => {
    const trimmed = query.trim()
    if (!trimmed) {
      setResults(null)
      return
    }
    setBusy(true)
    setError(null)
    const timer = setTimeout(async () => {
      try {
        const result = await api.get<{ fabrics: FabricSummary[] }>(
          `/fabrics?q=${encodeURIComponent(trimmed)}`,
        )
        setResults(result.fabrics)
      } catch (problem) {
        setError(problem)
      } finally {
        setBusy(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  return (
    <>
      <Link to="/" className="reference">
        ← Your requirements
      </Link>
      <h1 style={{ marginTop: 8 }}>Fabric catalog</h1>
      <p className="lede">
        Search by structure, fibre, yarn count or GSM — pick one to start a requirement from it.
      </p>
      <Problem error={error} />

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="e.g. single jersey 200 gsm"
        autoFocus
      />

      {busy ? <Loading /> : null}
      {!busy && !query.trim() ? <Empty>Type to search the catalogue.</Empty> : null}
      {!busy && query.trim() && results?.length === 0 ? (
        <Empty>No matches. Try fewer or different words.</Empty>
      ) : null}

      {results?.map((fabric) => (
        <Link key={fabric.id} className="card card-link" to={`/new?fabricId=${fabric.id}`}>
          <div className="card-head">
            <strong>{fabric.label}</strong>
          </div>
          <div className="reference">{fabric.fabricCode}</div>
          <ul className="specs">
            <li>
              GSM <b>{measurement(fabric.finishedGsm)}</b>
            </li>
            <li>
              Width <b>{measurement(fabric.finishedWidth, fabric.widthUnit)}</b>
            </li>
            {fabric.construction ? (
              <li>
                Construction <b>{fabric.construction}</b>
              </li>
            ) : null}
          </ul>
        </Link>
      ))}
    </>
  )
}
